/**
 * Breadcrumb Component
 *
 * Reusable breadcrumb navigation for detail/sub-pages.
 * Uses react-icons/ri to match the project's icon library.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { RiHomeLine, RiArrowRightSLine } from 'react-icons/ri';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { resolveLandingRoute } from '../../config/navigation';
import { buildMenuAccessChecker } from '../../utils/permissions';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const { permissions } = useAuth();
  const { isSuperAdmin, isSuperUser } = usePermission();
  // G-15: ไอคอน home ชี้เมนูแรกที่ role นี้เข้าได้จริง (ไม่ใช่ /dashboard ตายตัว —
  // role ที่ไม่มี dashboard.view กดแล้วจะเจอหน้า access denied) แบบเดียวกับ AccessDenied
  const homeTarget = resolveLandingRoute(
    buildMenuAccessChecker(permissions, isSuperAdmin || isSuperUser)
  );

  return (
    <nav className="flex items-center text-sm text-secondary-500 mb-4 overflow-x-auto" aria-label="Breadcrumb">
      <Link to={homeTarget} className="hover:text-primary-600 flex items-center transition-colors flex-shrink-0" aria-label="กลับไปหน้าหลัก">
        <RiHomeLine className="w-4 h-4" aria-hidden="true" />
      </Link>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <RiArrowRightSLine className="w-4 h-4 mx-1.5 text-secondary-400 flex-shrink-0" />
          {item.path ? (
            <Link
              to={item.path}
              className="hover:text-primary-600 hover:underline transition-colors whitespace-nowrap"
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-secondary-800 font-medium truncate max-w-[200px] sm:max-w-none sm:whitespace-nowrap" title={item.label}>{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumb;
